import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useQueries, useQuery } from "@tanstack/react-query";
import { FileDown, Loader2 } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, isAdmin, isVendor, setSandbox, type Board } from "../lib/api";
import { useConfig, useSession } from "../lib/hooks";
import { addDays, longDate } from "../lib/format";
import { FROM_COPOPT } from "../lib/days-ahead";
import {
  MAX_EXPORT_DAYS,
  PLAN_PARTS,
  dayClosures,
  exportDates,
  planFileTitle,
  planPrintUrl,
  printedAt,
  resourceRows,
  standGroups,
  standSummary,
  towLines,
  timeOn,
  versionText,
  type PlanExportRequest,
  type PlanPart,
  type ResourceRow,
} from "../lib/plan-export";
import { PrintHeader } from "./PrintHeader";

/**
 * The plan for one or more days as printed pages, for the teams who work to it without
 * the app open: the ramp (stands and gates), the check-in supervisor (counters) and the
 * bag room (reclaim and outbound belts). It opens in its own tab, landscape, one part of
 * one day to a page, and asks the browser to print; choosing Save as PDF in the print
 * window is how it becomes a file to send.
 *
 * Everything is in words as well as on the page's lines, so a black-and-white copy loses
 * nothing: a conflict says "Conflict", an overnight stay says "Overnight".
 */

const th = "border-b-2 border-black px-1.5 py-1 text-left text-[10px] font-bold tracking-wide uppercase";
const td = "border-b border-neutral-300 px-1.5 py-1 align-top";

/** Settings, Export PDF: pick the days and the parts, and the printable plan opens in a new tab. */
export function ExportPlanForm({ date, sandbox }: { date: string; sandbox: number | null }) {
  const [from, setFrom] = useState(date);
  const [days, setDays] = useState(1);
  const [parts, setParts] = useState<PlanPart[]>(PLAN_PARTS.map((p) => p.id));
  const toggle = (id: PlanPart) => setParts(parts.includes(id) ? parts.filter((p) => p !== id) : PLAN_PARTS.map((p) => p.id).filter((p) => p === id || parts.includes(p)));
  const open = () => {
    window.open(planPrintUrl({ from, days, parts, sandbox }), "_blank");
  };
  return (
    <div className="max-w-md rounded-xl border border-line bg-panel">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <FileDown className="size-4 text-ink-2" />
        <div className="flex-1 text-[13px] font-bold">Export the plan as PDF</div>
      </div>
      <div className="flex flex-col gap-3 px-3 py-3 text-[12.5px]">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5">
            From
            <input type="date" className="rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          </label>
          <label className="flex items-center gap-1.5">
            for
            <select className="rounded-md border border-line bg-panel px-1.5 py-1 text-[12.5px]" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {Array.from({ length: MAX_EXPORT_DAYS }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? "day" : "days"}
                </option>
              ))}
            </select>
          </label>
        </div>
        {days > 1 && <div className="-mt-2 text-[11.5px] text-ink-3">To {longDate(addDays(from, days - 1), "UTC")}</div>}
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">What to include</legend>
          {PLAN_PARTS.map((p) => (
            <label key={p.id} className="flex items-center gap-2">
              <input type="checkbox" checked={parts.includes(p.id)} onChange={() => toggle(p.id)} />
              {p.label}
            </label>
          ))}
        </fieldset>
        <p className="text-[11.5px] text-ink-3">
          Opens in a new tab, one page per part per day, ready to print. To send it as a file, choose <b>Save as PDF</b> as the printer.
        </p>
        <button
          className="rounded-lg bg-brand px-3 py-1.5 text-[13px] font-bold text-brand-ink shadow-sm hover:brightness-95 disabled:opacity-50"
          disabled={parts.length === 0}
          onClick={open}
        >
          Open printable plan
        </button>
      </div>
    </div>
  );
}

/** The new tab itself: reads the request from its address, loads each day's board, then prints. */
export function PlanPrintPage({ request }: { request: PlanExportRequest }) {
  // Before any query: the printout is of the same plan the board was showing.
  setSandbox(request.sandbox);
  const session = useSession();
  const user = session.data?.user ?? null;
  const allowed = !!user && !isVendor(user.role);
  const config = useConfig(allowed);
  const dates = exportDates(request.from, request.days);
  const boards = useQueries({
    queries: dates.map((d) => ({ queryKey: ["board", d], queryFn: () => api.board(d), enabled: allowed, staleTime: Infinity, refetchOnWindowFocus: false })),
  });
  const sandboxes = useQuery({ queryKey: ["sandboxes"], queryFn: () => api.sandboxes(), enabled: allowed && request.sandbox != null && !!user && isAdmin(user.role) });
  const sandboxName = request.sandbox == null ? null : (sandboxes.data?.sandboxes.find((s) => s.id === request.sandbox)?.name ?? `Sandbox ${request.sandbox}`);
  const loaded = boards.every((b) => b.data) && !!config.data;
  const failed = boards.find((b) => b.error)?.error ?? config.error;
  const [printed, setPrinted] = useState(() => Date.now());
  const asked = useRef(false);

  const tz = boards[0]?.data?.airport.timezone ?? config.data?.timezone ?? "UTC";
  const title = planFileTitle(config.data?.iata ?? "", dates);
  useEffect(() => {
    if (!loaded || asked.current) return;
    asked.current = true;
    document.title = title;
    // A moment for the tables to lay out before the print window measures them.
    const id = setTimeout(() => window.print(), 400);
    return () => clearTimeout(id);
  }, [loaded, title]);
  useEffect(() => {
    const before = () => setPrinted(Date.now());
    window.addEventListener("beforeprint", before);
    return () => window.removeEventListener("beforeprint", before);
  }, []);

  if (session.isLoading) return <Waiting text="Checking you are signed in…" />;
  if (!user) return <Waiting text="Sign in to Vectro Allocate in another tab, then export the plan again." still />;
  if (!allowed) return <Waiting text="This account has no board to print." still />;
  if (failed) return <Waiting text={`The plan could not be loaded: ${failed instanceof Error ? failed.message : "unknown error"}. Close this tab and try again.`} still />;
  if (!loaded) return <Waiting text={`Loading the plan for ${dates.length === 1 ? "the day" : `${dates.length} days`}…`} />;

  return (
    <div className="min-h-full bg-white text-black">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-neutral-300 bg-white px-4 py-2 text-[12.5px] print:hidden">
        <div className="font-semibold">{title}</div>
        <div className="text-neutral-600">Choose Save as PDF as the printer to keep it as a file. Landscape is set for you.</div>
        <button className="ml-auto flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 font-bold text-brand-ink" onClick={() => window.print()}>
          <FileDown className="size-4" /> Print or save as PDF
        </button>
        <button className="rounded-lg border border-neutral-300 px-3 py-1.5 font-semibold" onClick={() => window.close()}>
          Close
        </button>
      </div>
      <div className="mx-auto max-w-[1100px] p-4 print:max-w-none print:p-0">
        <PlanDocument boards={boards.map((b) => b.data!)} config={config.data!} parts={request.parts} printed={printedAt(printed, tz)} by={user.name} sandbox={sandboxName} />
      </div>
    </div>
  );
}

function Waiting({ text, still }: { text: string; still?: boolean }) {
  return (
    <div className="grid h-full place-items-center bg-white p-6 text-center text-[14px] text-neutral-700">
      <div className="flex items-center gap-2">
        {!still && <Loader2 className="size-5 animate-spin" />}
        {text}
      </div>
    </div>
  );
}

/** Every day asked for, each part on pages of its own. Pure, so it can be checked without a browser. */
export function PlanDocument({
  boards,
  config,
  parts,
  printed,
  by,
  sandbox,
}: {
  boards: Board[];
  config: AirportConfig;
  parts: PlanPart[];
  printed: string;
  by?: string | null;
  sandbox?: string | null;
}) {
  const pages = boards.flatMap((board) => parts.map((part) => ({ board, part })));
  return (
    <>
      {pages.map(({ board, part }, i) => (
        <section key={`${board.date}|${part}`} className={clsx("text-black", i > 0 && "mt-10 break-before-page print:mt-0")} data-part={part} data-date={board.date}>
          <DayHeader board={board} part={part} printed={printed} by={by} sandbox={sandbox} />
          {part === "stands" ? (
            <StandsPage board={board} config={config} />
          ) : (
            <ResourcePage board={board} config={config} kind={part} />
          )}
        </section>
      ))}
    </>
  );
}

function DayHeader({ board, part, printed, by, sandbox }: { board: Board; part: PlanPart; printed: string; by?: string | null; sandbox?: string | null }) {
  const tz = board.airport.timezone;
  const detail = [
    versionText(board),
    board.projectedFrom
      ? `Beyond the airport feed: these flights are a projection for planning (from ${board.projectedFrom === FROM_COPOPT ? "CopOpt's schedule" : longDate(board.projectedFrom, tz)}), and may change.`
      : "",
    sandbox ? `SANDBOX "${sandbox}": a trial plan, not the live one.` : "",
  ].filter(Boolean);
  return (
    <PrintHeader
      airport={`${board.airport.iata} · ${board.airport.name}`}
      title={`${PLAN_PARTS.find((p) => p.id === part)!.label} · ${longDate(board.date, tz)} ${board.date.slice(0, 4)}`}
      detail={detail}
      printed={printed}
      by={by}
    />
  );
}

function Time({ t }: { t: { scheduled: string; latest: string | null } }) {
  return (
    <>
      <span className="tabular font-semibold">{t.scheduled}</span>
      {t.latest && <div className="tabular text-[10px]">{t.latest}</div>}
    </>
  );
}

function StandsPage({ board, config }: { board: Board; config: AirportConfig }) {
  const tz = board.airport.timezone;
  const s = standSummary(board);
  const groups = standGroups(board, config);
  const tows = towLines(board);
  const closures = dayClosures(board);
  return (
    <>
      <p className="mb-2 text-[12px]">
        {s.turns} aircraft visits: {s.arrivals} arrivals, {s.departures} departures
        {s.overnight ? `, ${s.overnight} overnight` : ""}. {s.noStand ? `${s.noStand} without a stand. ` : "Every visit has a stand. "}
        {s.hard ? `${s.hard} conflict${s.hard === 1 ? "" : "s"} to resolve` : "No conflicts"}
        {s.warnings ? `, ${s.warnings} to check` : ""}. Times are local; a time on another day carries its day. A * after the type means it was assumed.
      </p>
      {(tows.length > 0 || closures.length > 0) && (
        <div className="mb-2 grid gap-x-6 text-[11.5px] sm:grid-cols-2 print:grid-cols-2">
          {tows.length > 0 && (
            <div>
              <div className="font-bold">Tows planned</div>
              {tows.map((t, i) => (
                <div key={i}>
                  {t.at} · {t.flight} to {t.toStand}
                  {t.note ? ` (${t.note})` : ""}
                </div>
              ))}
            </div>
          )}
          {closures.length > 0 && (
            <div>
              <div className="font-bold">Stands closed</div>
              {closures.map((c) => (
                <div key={c.id}>
                  {config.stands.find((x) => x.id === c.stand)?.name ?? c.stand} · {timeOn(c.from, tz, board.date)}–{timeOn(c.to, tz, board.date)}
                  {c.reason ? ` (${c.reason})` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            <th className={th}>Arrival</th>
            <th className={th}>From</th>
            <th className={th}>Arrives</th>
            <th className={th}>Departure</th>
            <th className={th}>To</th>
            <th className={th}>Departs</th>
            <th className={th}>On stand</th>
            <th className={th}>Aircraft</th>
            <th className={th}>Notes</th>
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.label} className="break-inside-avoid">
            <tr>
              <td colSpan={9} className="border-b border-black bg-neutral-100 px-1.5 pt-2 pb-0.5 text-[11.5px] font-bold">
                {g.label}
                {g.gate && g.gate !== g.label ? ` · ${g.gate}` : ""}
                <span className="font-normal">
                  {" "}
                  · {g.rows.length ? `${g.rows.length} visit${g.rows.length === 1 ? "" : "s"}` : "free all day"}
                </span>
              </td>
            </tr>
            {g.rows.map((r) => (
              <tr key={r.turnId} className={clsx("break-inside-avoid", r.hard && "font-semibold")}>
                <td className={clsx(td, "font-semibold", r.arr?.cancelled && "line-through")}>{r.arr?.flight ?? "—"}</td>
                <td className={td}>{r.arr?.from ?? ""}</td>
                <td className={td}>{r.arr ? <Time t={r.arr.time} /> : ""}</td>
                <td className={clsx(td, "font-semibold", r.dep?.cancelled && "line-through")}>{r.dep?.flight ?? "—"}</td>
                <td className={td}>{r.dep?.to ?? ""}</td>
                <td className={td}>{r.dep ? <Time t={r.dep.time} /> : ""}</td>
                <td className={clsx(td, "tabular whitespace-nowrap")}>{r.onStand}</td>
                <td className={td}>
                  {r.aircraft}
                  {r.registration && <div className="text-[10px]">{r.registration}</div>}
                </td>
                <td className={clsx(td, "max-w-[22rem]")}>{r.notes.join(" · ")}</td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </>
  );
}

const RESOURCE_WORDS = {
  counters: { other: "To", time: "Departs", where: "Counters", open: "Counters open", extra: "Booked", none: "No departures have check-in counters on this day." },
  belts: { other: "From", time: "Arrives", where: "Belt", open: "Bags on the belt", extra: "Bags", none: "No arrivals have a reclaim belt on this day." },
  outbound: { other: "To", time: "Departs", where: "Belt", open: "Make-up", extra: "Bags", none: "No departures have an outbound belt on this day." },
} as const;

function ResourcePage({ board, config, kind }: { board: Board; config: AirportConfig; kind: "counters" | "belts" | "outbound" }) {
  const words = RESOURCE_WORDS[kind];
  if (kind === "outbound" && !(config.outboundBelts ?? []).some((b) => b.active)) {
    return <p className="text-[12px]">No outbound belts are set up. An administrator can add them in Settings.</p>;
  }
  const rows = resourceRows(board, config, kind);
  if (rows.length === 0) return <p className="text-[12px]">{words.none}</p>;
  const unassigned = rows.filter((r) => r.where === "Not assigned").length;
  return (
    <>
      <p className="mb-2 text-[12px]">
        {rows.length} flight{rows.length === 1 ? "" : "s"}, earliest first.{" "}
        {unassigned ? `${unassigned} not assigned yet.` : "Every one is assigned."} Times are local. &ldquo;Set by a planner&rdquo; means a person chose it, so the app will not move it.
      </p>
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            <th className={th}>Flight</th>
            <th className={th}>{words.other}</th>
            <th className={th}>{words.time}</th>
            <th className={th}>{words.where}</th>
            <th className={th}>{words.open}</th>
            <th className={th}>{words.extra}</th>
            <th className={th}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <ResourceLine key={r.legId} r={r} />
          ))}
        </tbody>
      </table>
    </>
  );
}

function ResourceLine({ r }: { r: ResourceRow }) {
  const notes = [r.cancelled ? "Cancelled" : "", r.pinned ? "Set by a planner" : ""].filter(Boolean);
  return (
    <tr className={clsx("break-inside-avoid", r.where === "Not assigned" && "font-semibold")}>
      <td className={clsx(td, "font-semibold", r.cancelled && "line-through")}>{r.flight}</td>
      <td className={td}>{r.other ?? ""}</td>
      <td className={td}>
        <Time t={r.time} />
      </td>
      <td className={clsx(td, "font-semibold")}>{r.where}</td>
      <td className={clsx(td, "tabular whitespace-nowrap")}>{r.open}</td>
      <td className={td}>{r.extra ?? ""}</td>
      <td className={td}>{notes.join(" · ")}</td>
    </tr>
  );
}
